import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, PenSquare, Trash2, Eye, EyeOff, Edit3, Plus } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth.store';
import { useMyBlogPosts, useUpdateBlogPost, useDeleteBlogPost } from '@/hooks/useBlog';

export function MyBlogs() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useMyBlogPosts({ page, limit: 20 });
  const updatePost = useUpdateBlogPost();
  const deletePost = useDeleteBlogPost();

  if (user?.role !== 'ADMIN') {
    return <Navigate to="/blog" replace />;
  }

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    await updatePost.mutateAsync({ id, data: { status: newStatus as 'draft' | 'published' } });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('blogPage.deleteConfirm'))) {
      await deletePost.mutateAsync(id);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-4xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('blogPage.backToBlog')}
          </Link>

          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-bold">{t('blogPage.myPosts')}</h1>
            <Link to="/blog/create">
              <Button className="gap-2" size="sm">
                <Plus className="h-4 w-4" />
                {t('blogPage.createPost')}
              </Button>
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse rounded-lg border p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-24 bg-muted rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 w-2/3 bg-muted rounded" />
                      <div className="h-4 w-1/3 bg-muted rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : data && data.data.length > 0 ? (
            <div className="space-y-3">
              {data.data.map((post) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-4 rounded-lg border bg-card p-4 hover:shadow-sm transition-shadow"
                >
                  {/* Thumbnail */}
                  {post.coverImage ? (
                    <img
                      src={post.coverImage}
                      alt=""
                      className="h-16 w-24 rounded-md object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-16 w-24 rounded-md bg-gradient-to-br from-emerald-500 to-teal-500 shrink-0 flex items-center justify-center">
                      <PenSquare className="h-6 w-6 text-white/50" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/blog/${post.slug}`} className="text-sm font-semibold hover:text-emerald-600 line-clamp-1">
                      {post.title}
                    </Link>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                        post.status === 'published'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {post.status === 'published' ? t('blogPage.published') : t('blogPage.draft')}
                      </span>
                      <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                      <span>{post.viewCount} {t('blogPage.views')}</span>
                      <span>{post.commentCount} {t('blogPage.commentsCount')}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleToggleStatus(post.id, post.status)}
                      title={post.status === 'published' ? t('blogPage.unpublish') : t('blogPage.publish')}
                    >
                      {post.status === 'published' ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Link to={`/blog/edit/${post.id}`}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(post.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="flex justify-center gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    {t('common.back')}
                  </Button>
                  <span className="text-sm text-muted-foreground flex items-center px-3">
                    {page} / {data.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    {t('common.next')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <PenSquare className="h-8 w-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-medium mb-1">{t('blogPage.noPosts')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t('blogPage.noPostsDescription')}</p>
              <Link to="/blog/create">
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  {t('blogPage.createPost')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
